'use client';

import { useEffect } from 'react';
import { useThemePrefsStore, MONO_PRESET_ID } from '@/shared/stores/theme-prefs-store';
import { hexToRgbTriplet, getSurfaceMutedHex, type Shade } from '@softfactura/ui';
import { TABLE_HEADER_PRESETS } from '@/shared/constants/table-header-styles';

/**
 * Montat o singură dată în providers.tsx. Citește scale-ul curent din
 * theme-prefs-store și setează CSS variables --primary-50 ... --primary-900
 * pe <html>, astfel încât toate clasele Tailwind primary să reflecte
 * instant culoarea aleasă. Atașează și clasa `theme-clean` pe <html> când
 * presetul activ e „mono", ca să active regulile globale care suprimă
 * box-shadow-urile (vezi globals.css).
 */
export function ThemeApplier() {
  const scale = useThemePrefsStore((s) => s.scale);
  const bgTone = useThemePrefsStore((s) => s.bgTone);
  const presetId = useThemePrefsStore((s) => s.presetId);
  const cornerStyle = useThemePrefsStore((s) => s.cornerStyle);
  const tableHeaderStyle = useThemePrefsStore((s) => s.tableHeaderStyle);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    const shades: Shade[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
    for (const shade of shades) {
      const hex = scale[shade];
      if (!hex) continue;
      root.style.setProperty(`--primary-${shade}`, hexToRgbTriplet(hex));
    }
    root.style.setProperty('--surface-muted', getSurfaceMutedHex(scale, bgTone));
    root.classList.toggle('theme-clean', presetId === MONO_PRESET_ID);
    root.classList.toggle('theme-square', cornerStyle === 'square');

    // Aplica preset-ul de header tabel — CSS vars `--v-th-*` consumate de
    // .v-table thead și .v-data-table-skin thead în vanzari.css.
    const tableHeaderPreset = TABLE_HEADER_PRESETS[tableHeaderStyle] || TABLE_HEADER_PRESETS['light-bordered'];
    for (const [k, v] of Object.entries(tableHeaderPreset.cssVars)) {
      root.style.setProperty(k, v);
    }
  }, [scale, bgTone, presetId, cornerStyle, tableHeaderStyle]);

  return null;
}
